import { BoardUserActivityType } from '@/api/workspaceSettings/queries';
import { uuid } from '@/_core/utils/uuid';
import {
    AddPersonasToCard,
    UpdatePersonaName as UpdatePersonaNameCommand,
    UpdatePersonaGroup as UpdatePersonaGroupCommand,
    UpdatePersonaDescription as UpdatePersonaDescriptionCommand,
    DeletePersona as DeletePersonaCommand,
    UpdatePersonaCustomFieldOnStorymap,
    UpdatePersonaPersonalityFieldOnStorymap,
    UpdatePersonaPicture,
    CreatePersona,
} from '..';
import * as storymapStoreModule from '../../../store';
import {
    AddPersonaToCard,
    RemovePersonaFromCard,
    CreateNewPersona,
    DeletePersona,
    SelectPersonaAvatar,
    UpdatePersonaCustomField,
    UpdatePersonaName,
} from '../../../store';
import { getCardById } from '../../../store/use';
import orderBy from 'lodash/orderBy';
import { UpdatePersonaGroup } from '../../../store/actions/persona/updatePersonaGroup';
import { UpdatePersonaDescription } from '../../../store/actions/persona/updatePersonaDescription';
import { UpdatePersonaPersonality } from '../../../store/actions/persona/updatePersonaPersonality';

export const createAddPersonaToCardCommand = async (action: AddPersonaToCard) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const card = getCardById(action.cardId);

    const activityId = await uuid.create();

    const oldPersonas = state.personas.filter((x) => card.personaIds.includes(x.id));

    const addedPersona = state.personas.find((x) => x.id === action.personaId);
    const addedPersonaPicture = state.personaPictures.find((x) => x.path === addedPersona.picture);

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        CardId: card.id,
        CardName: card.title,
        CardPersonas: addedPersona.name,
        OriginalContent: oldPersonas.map((x) => x.name).join(', '),
        PersonaId: addedPersona.id,
        PersonaName: addedPersona.name,
        PersonaPictureId: addedPersonaPicture?.id,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonasAssignedToActivity,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const orderedPersonas = orderBy([...oldPersonas, addedPersona], 'name');

    const cmd = new AddPersonasToCard(
        state.id,
        card.id,
        orderedPersonas.map((x) => x.id)
    );

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createRemovePersonaFromCardCommand = async (action: RemovePersonaFromCard) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const card = getCardById(action.cardId);

    const activityId = await uuid.create();

    const oldPersonas = state.personas.filter((x) => card.personaIds.includes(x.id));

    const removedPersona = state.personas.find((x) => x.id === action.personaId);
    const removedPersonaPicture = state.personaPictures.find((x) => x.path === removedPersona.picture);

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        CardId: card.id,
        CardName: card.title,
        CardPersonas: removedPersona.name,
        OriginalContent: oldPersonas.map((x) => x.name).join(', '),
        PersonaId: removedPersona.id,
        PersonaName: removedPersona.name,
        PersonaPictureId: removedPersonaPicture?.id,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonasUnassignedFromActivity,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const orderedPersonas = orderBy(
        oldPersonas.filter((x) => x.id !== removedPersona.id),
        'name'
    );

    const cmd = new AddPersonasToCard(
        state.id,
        card.id,
        orderedPersonas.map((x) => x.id)
    );

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createRenamePersonaCommand = async (action: UpdatePersonaName) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: action.name,
        OriginalContent: persona.name,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaNameChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaNameCommand(state.id, action.id, action.name);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createCreateNewPersonaCommand = async (action: CreateNewPersona) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);
    const defaultPersonaPicture = state.personaPictures.find((x) => x.id == '0');

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: action.name,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaAdded,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new CreatePersona(
        state.id,
        action.id,
        action.name,
        action.pictureId ?? defaultPersonaPicture.id,
        action.group,
        action.description,
        action.customFieldsToSave,
        action.personalitiesToSave
    );

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createUpdatePersonaGroupCommand = async (action: UpdatePersonaGroup) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        PersonaGroup: action.group,
        OriginalContent: persona.group,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaGroupChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaGroupCommand(state.id, action.id, action.group);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createUpdatePersonaDescriptionCommand = async (action: UpdatePersonaDescription) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        PersonaDescription: action.description,
        OriginalContent: persona.description,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaDescriptionChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaDescriptionCommand(state.id, action.id, action.description);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createUpdatePersonaCustomFieldCommand = async (action: UpdatePersonaCustomField) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);
    const customField = persona.customFields.find((x) => x.id == action.customFieldId);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        Content: `${customField.label}: ${action.value}`,
        OriginalContent: customField?.value,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaCustomFieldChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaCustomFieldOnStorymap(state.id, action.id, action.customFieldId, action.value);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createUpdatePersonaPersonalityCommand = async (action: UpdatePersonaPersonality) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);
    const personalityField = persona.personalities.find((x) => x.id == action.personalityFieldId);
    const newContent = personalityField.endingLabel
        ? `${personalityField.startingLabel} - ${personalityField.endingLabel}: ${action.value}`
        : `${personalityField.startingLabel}: ${action.value}`;

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        Content: newContent,
        OriginalContent: personalityField?.value,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaPersonalityFieldChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaPersonalityFieldOnStorymap(state.id, action.id, action.personalityFieldId, action.value);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createUpdatePersonaPictureCommand = async (action: SelectPersonaAvatar) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);
    const oldPicture = state.personaPictures.find((x) => x.path == persona.picture);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        PersonaPictureId: action.avatarId,
        OriginalContent: oldPicture?.id,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaPictureChanged,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new UpdatePersonaPicture(state.id, action.id, action.avatarId);

    cmd.BoardActivity = boardActivity;

    return cmd;
};

export const createDeletePersonaCommand = async (action: DeletePersona) => {
    const store = storymapStoreModule.useStorymapStore();
    const state = store.state;

    const persona = state.personas.find((x) => x.id === action.id);

    const activityId = await uuid.create();

    const boardActivity: any = {
        Datetime: new Date().toJSON(),
        EstimationUnit: '',
        Id: activityId,
        StorymapId: state.id,
        PersonaId: persona.id,
        PersonaName: persona.name,
        WorkspaceId: initAppData.currentWs.workspaceId,
        Type: BoardUserActivityType.PersonaDeleted,
        UserId: initAppData.currentUser.currentUserShortId,
        Username: initAppData.currentUser.currentUserFullName,
    };

    const cmd = new DeletePersonaCommand(state.id, action.id);

    cmd.BoardActivity = boardActivity;

    return cmd;
};
